/*
 * Sitewide Sale Display
 *
 * Companion to the Liquid changes in snippets/price.liquid and snippets/card-price.liquid.
 *
 * Why this exists:
 *   The theme's hdt-price and hdt-compare-at-price web components update their
 *   contents on every `variant:change` event by reading the raw variant payload
 *   (variant.price / variant.compare_at_price). Our Liquid phantom override
 *   only affects the initial server render, so without this script a user
 *   switching size/colour on a PDP during a sitewide sale would see the
 *   strikethrough disappear and the price revert to RRP.
 *
 * What it does:
 *   Registers a capture-phase listener on `variant:change`. Before the price
 *   components see the event, we rewrite e.detail.variant so that:
 *     - variant.price = original × (1 - pct/100)
 *     - variant.compare_at_price = original
 *     - variant.variant_state.on_sale = true
 *   The default handlers then render the phantom values exactly as if Shopify
 *   had a real compare-at price set.
 *
 * What it skips:
 *   - Variants whose product is in the excluded-IDs list (gift card, re:do
 *     returns product, the four fitting-appointment products). These are
 *     resolved from handles in the inline config that loads this script.
 *   - Variants that are already on a real sale (variant.compare_at_price >
 *     variant.price). The native sale wins; we don't stack.
 *
 * Configuration is supplied via window.BravaSitewideSale, populated in
 * snippets/sitewide-sale-data.liquid (rendered from layout/theme.liquid).
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var cfg = window.BravaSitewideSale;
  if (!cfg || cfg.enabled !== true) return;

  var pct = parseFloat(cfg.percent);
  if (isNaN(pct) || pct <= 0 || pct >= 100) return;

  var factor = (100 - pct) / 100;
  var excluded = new Set(Array.isArray(cfg.excludedIds) ? cfg.excludedIds : []);

  function applyPhantom(variant) {
    if (!variant || typeof variant.price !== 'number') return;
    if (excluded.has(variant.product_id)) return;

    // Skip if this variant is already on a real sale — native display wins.
    if (variant.compare_at_price && variant.compare_at_price > variant.price) return;

    var original = variant.price;
    // Round half-up to whole cents, matching the Liquid math
    //   price | times: factor_num | divided_by: 100
    // (Liquid integer-divides, so the cent-level result may diverge by one
    //  cent on rare boundaries; this is acceptable for display purposes and
    //  is documented in the handoff.)
    var salePrice = Math.floor((original * (100 - pct)) / 100);

    variant.compare_at_price = original;
    variant.price = salePrice;
    if (variant.variant_state && typeof variant.variant_state === 'object') {
      variant.variant_state.on_sale = true;
    }
  }

  document.addEventListener(
    'variant:change',
    function (e) {
      if (!e || !e.detail) return;
      applyPhantom(e.detail.variant);
    },
    true /* useCapture: fire before the price components' bubble-phase handlers */
  );
})();
